// Medlemskaber: besøg og automatiske korttræk.
//
// Et medlemskab har et antal besøg pr. år. Freemi planlægger besøgene for et helt medlemsår og
// et træk pr. besøg på besøgets dato. Flyttes et besøg, flyttes dets træk med. De andre bliver.
// Ved årets udløb planlægges næste år automatisk (ingen manuel oprettelse hvert år).
import { plusDage } from './dato.ts';

export { plusDage };   // bruges mange steder sammen med medlemskabets datoer (selve funktionen ligger i dato.ts)

export const INTERVALLER = [
  { besoeg: 12, navn: 'Hver måned' },
  { besoeg: 6, navn: 'Hver 2. måned' },
  { besoeg: 4, navn: 'Hver 3. måned' },
  { besoeg: 3, navn: 'Hver 4. måned' },
  { besoeg: 2, navn: 'Hver 6. måned' },
  { besoeg: 1, navn: 'En gang om året' },
] as const;

/** Kontorets intervaller: også hver uge / 2. uge / 4. uge (regnes i dage). Hjemmesidens medlemstilbud bruger kun INTERVALLER. */
export const KONTOR_INTERVALLER = [
  { besoeg: 52, navn: 'Hver uge' }, { besoeg: 26, navn: 'Hver 2. uge' }, { besoeg: 13, navn: 'Hver 4. uge' }, ...INTERVALLER,
] as const;
export const UGE_INTERVAL = (besoeg: number) => besoeg === 52 || besoeg === 26 || besoeg === 13;

export function intervalNavn(besoeg: number): string {
  return KONTOR_INTERVALLER.find((i) => i.besoeg === besoeg)?.navn ?? `${besoeg} gange om året`;
}

function tilDato(d: Date): string { return d.toISOString().slice(0, 10); }
function fra(dato: string): Date { const [y, m, d] = dato.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); }

/** Lægger hele måneder til og holder dagen inden for måneden (31. jan + 1 md = 28./29. feb). */
export function plusMaaneder(dato: string, n: number): string {
  const d = fra(dato);
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + n, dag = d.getUTCDate();
  const sidste = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return tilDato(new Date(Date.UTC(y, m, Math.min(dag, sidste))));
}

/** Lørdag/søndag flyttes til mandag, så forslaget lander på en arbejdsdag – men aldrig ind i næste måned (så fredag),
 *  så et månedligt medlemskab altid får ét besøg pr. måned (som pixie_medlem_dato i databasen). */
export function hverdag(dato: string): string {
  const w = fra(dato).getUTCDay();
  if (w !== 6 && w !== 0) return dato;
  const frem = plusDage(dato, w === 6 ? 2 : 1);
  return frem.slice(0, 7) === dato.slice(0, 7) ? frem : plusDage(dato, w === 6 ? -1 : -2);
}

/** Datoer for besøgene i ét medlemsår, startende med `start`. */
export function besoegsDatoer(start: string, besoegPrAar: number): string[] {
  if (!KONTOR_INTERVALLER.some((i) => i.besoeg === besoegPrAar)) throw new Error('Ugyldigt interval.');
  // Uge-intervallerne holder samme ugedag (som pixie_medlem_dato i databasen)
  if (UGE_INTERVAL(besoegPrAar)) return Array.from({ length: besoegPrAar }, (_, i) => plusDage(start, i * (364 / besoegPrAar)));
  const skridt = 12 / besoegPrAar;
  return Array.from({ length: besoegPrAar }, (_, i) => hverdag(plusMaaneder(start, i * skridt)));
}

export type PlanlagtTraek = { besoeg_nr: number; dato: string; beloeb: number; status: 'planlagt' | 'trukket' | 'fejlet' | 'annulleret' };


/** Datoer for en gentaget booking: hver `uger` uge, `antal` gange i alt (inkl. den første). */
export function gentagDatoer(start: string, uger: number, antal: number): string[] {
  if (!(uger >= 1 && uger <= 52) || !(antal >= 2 && antal <= 52)) throw new Error('Vælg mellem 1 og 52 uger og 2 til 52 gange.');
  return Array.from({ length: antal }, (_, i) => plusDage(start, i * uger * 7));
}

// ---------- Medlemskab fra hjemmesiden (gratis, betal efter hvert besøg) ----------
/** Rabat i % pr. antal besøg om året. Kræver mindst `minBesoeg` besøg om året. */
/** minimum: minBesoeg besøg inden for `maaneder` måneder. Opsiges medlemskabet før, betaler kunden normalpris for de udførte vaske
 *  (efterregning), medmindre den frafaldes (frafald: hvem må – kontoret eller kun ejer/admin). */
export type MedlemsMinimum = { maaneder: number; efterregning: boolean; frafald: 'kontor' | 'ejer' };
export const STANDARD_MINIMUM: MedlemsMinimum = { maaneder: 12, efterregning: true, frafald: 'kontor' };
export type MedlemsIndst = { aktiv: boolean; rabat: Record<string, number>; minBesoeg: number; vilkaar: string; minimum?: MedlemsMinimum };

/** Efterregning ved opsigelse før minimum: (normalpris − medlemspris) for hver udført vask. 0, når minimum er nået, eller der intet er udført. */
export function efterregning(a: { udfoert: number; minimum: number; normalInkl: number; medlemInkl: number }): number {
  if (a.udfoert >= a.minimum || a.udfoert <= 0) return 0;
  return Math.max(0, a.normalInkl - a.medlemInkl) * a.udfoert;
}
export const STANDARD_MEDLEM: MedlemsIndst = {
  aktiv: true, rabat: { '12': 42, '6': 30, '4': 20, '3': 15, '2': 10 }, minBesoeg: 2,
  vilkaar: 'Det er gratis at være medlem. Du forpligter dig til mindst 2 vask om året og betaler først, når vasken er udført. Medlemskabet kan opsiges med 1 måneds varsel.',
};
export type MedlemsTilbud = { besoeg: number; navn: string; rabatProcent: number; normalInkl: number; medlemInkl: number; sparPrBesoeg: number; sparPrAar: number };
/** Medlemspriser (prisEx = pakke + valgte tilvalg, ekskl. moms). Rabatten gælder det hele. Beløb i øre inkl. moms. */
export function medlemsTilbud(prisEx: number, ind: MedlemsIndst, momsProcent = 25, intervaller: readonly { besoeg: number; navn: string }[] = INTERVALLER): MedlemsTilbud[] {
  const inkl = (ex: number) => ex + Math.round((ex * momsProcent) / 100);
  return intervaller.filter((i) => i.besoeg >= ind.minBesoeg && ind.rabat[String(i.besoeg)] != null).map((i) => {
    const pct = ind.rabat[String(i.besoeg)];
    const normal = inkl(prisEx), medlem = inkl(prisEx - Math.round((prisEx * pct) / 100));
    return { besoeg: i.besoeg, navn: i.navn, rabatProcent: pct, normalInkl: normal, medlemInkl: medlem, sparPrBesoeg: normal - medlem, sparPrAar: (normal - medlem) * i.besoeg };
  });
}
